import { useCallback } from 'react';

import {
	type CopyLinkButtonClickedArgs,
	onCopyLinkButtonClicked,
} from '../../../common/utils/events/copy-link-button-clicked';
import { getExtensionData } from '../../../common/utils/relay/get-extension-data';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../analytics';
import type { SharedEventAttributes } from '../../analytics/prepared-result-attributes';
import type { AnalyticsEventCallbacks } from '../../analytics/result-analytics-callbacks';

interface FireCopyLinkButtonClickedProps {
	location: CopyLinkButtonClickedArgs['location'];
}

/**
 * @returns a callback to fire an event when the result
 * is clicked by the user.
 */

export const useFireCopyLinkButtonClickedWithAttributes = (
	sharedEventAttributes: SharedEventAttributes,
	eventCallbacks: AnalyticsEventCallbacks,
	copyLinkEventAttributes: FireCopyLinkButtonClickedProps,
): (() => void) => {
	const { location } = copyLinkEventAttributes;
	const { commonAttributes, nonPrivacySafeAttributes } = sharedEventAttributes;
	const { fireAnalyticsEvent } = eventCallbacks;
	return useCallback(() => {
		fireAnalyticsEvent(
			onCopyLinkButtonClicked({
				commonAttributes: {
					...commonAttributes,
					...getExtensionData(commonAttributes.isCQL),
				},
				nonPrivacySafeAttributes,
				location,
			}),
		);
	}, [fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes, location]);
};

export const useFireCopyLinkButtonClicked = (
	location: CopyLinkButtonClickedArgs['location'],
): (() => void) => {
	const sharedEventAttributes = useCommonAndPrivateAttributes();
	const { fireAnalyticsEvent } = useAnalytics();
	return useFireCopyLinkButtonClickedWithAttributes(
		sharedEventAttributes,
		{ fireAnalyticsEvent },
		{ location },
	);
};
